import { useRef } from 'react';
import depthArtwork from '../assets/doors/measurement-depth.svg';

export function DepthClearanceHelp() {
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={opener}
        type="button"
        data-name="depth-help"
        aria-label="How to check depth clearance"
        aria-haspopup="dialog"
        aria-controls="depth-help-dialog"
        onClick={() => dialog.current?.showModal()}
        className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-brand focus-visible:outline-2 focus-visible:outline-brand"
      >
        <svg
          aria-hidden="true"
          className="size-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.6 9.2a2.5 2.5 0 0 1 4.8 1c0 1.7-2.4 2.1-2.4 3.6" strokeLinecap="round" />
          <circle cx="12" cy="17.2" r=".6" fill="currentColor" stroke="none" />
        </svg>
      </button>
      <dialog
        ref={dialog}
        id="depth-help-dialog"
        aria-labelledby="depth-help-title"
        aria-describedby="depth-help-description"
        onClose={() => opener.current?.focus()}
        onKeyDown={(event) => {
          if (event.key === 'Tab') {
            // Close is the dialog's only focusable control.
            event.preventDefault();
            closeButton.current?.focus();
          }
        }}
        className="fixed inset-0 m-auto max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto rounded-xl bg-white p-6 text-ink shadow-xl backdrop:bg-black/60"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id="depth-help-title" className="text-sm font-semibold">
            How to check depth clearance
          </h2>
          <button
            ref={closeButton}
            type="button"
            data-name="depth-help-close"
            onClick={() => dialog.current?.close()}
            className="min-h-11 min-w-11 rounded-md border border-gray-300 px-2 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-brand"
          >
            Close
          </button>
        </div>
        <p id="depth-help-description" className="mt-3 text-sm font-semibold">
          First, close your existing door before measuring.
        </p>
        <img
          src={depthArtwork}
          alt="Top-down view of a closed door showing A to the door face and B to the furthest part of the handle."
          className="mx-auto mt-4 h-auto w-full max-w-lg"
        />
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <p>
            <strong>A:</strong> From the front edge of the frame to the outside face of the closed
            door.
          </p>
          <p>
            <strong>B:</strong> From the same front edge to the furthest protruding part of the
            existing handle, knob, deadbolt or other fitting.
          </p>
        </div>
        <p className="mt-4 text-sm">
          The diagram&apos;s 56 mm and 23 mm values are illustrative only. Enter B in the planner;
          do not add A and B.
        </p>
      </dialog>
    </>
  );
}
